import { opaqueHex, parseHex } from "../scene/color";
import type { ClipColor } from "../scene/schema";

/**
 * Colour correction of clips (docs/05-video-und-untertitel.md §3a). All
 * sliders together form one affine colour matrix on sRGB values (0..1). The
 * editor draws it as an SVG `feColorMatrix` (colour-interpolation sRGB), the
 * export as ffmpeg `colorchannelmixer` – the same numbers, so preview and MP4
 * look alike. The vignette is a radial gradient over the clip's area, drawn
 * with the same stops on the canvas and as a PNG in the export.
 */

export type ColorAdjustment = Exclude<keyof ClipColor, "look" | "duotone">;

export const COLOR_ADJUSTMENTS: readonly ColorAdjustment[] = [
  "brightness",
  "contrast",
  "saturation",
  "temperature",
  "tint",
  "fade",
  "vignette",
];

export const COLOR_ADJUSTMENT_LABELS: Record<ColorAdjustment, string> = {
  brightness: "Helligkeit",
  contrast: "Kontrast",
  saturation: "Sättigung",
  temperature: "Wärme",
  tint: "Farbton",
  fade: "Verblassen",
  vignette: "Vignette",
};

/** Slider range per adjustment (fade and vignette only go one way). */
export const COLOR_ADJUSTMENT_MIN: Record<ColorAdjustment, number> = {
  brightness: -1,
  contrast: -1,
  saturation: -1,
  temperature: -1,
  tint: -1,
  fade: 0,
  vignette: 0,
};

export const NEUTRAL_COLOR: ClipColor = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  temperature: 0,
  tint: 0,
  fade: 0,
  vignette: 0,
};

/** A full set of values (older or partial input gets the neutral defaults). */
export function normalizeColor(color: Partial<ClipColor> | null | undefined): ClipColor {
  const out: ClipColor = { ...NEUTRAL_COLOR };
  for (const key of COLOR_ADJUSTMENTS) {
    const value = color?.[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      out[key] = Math.max(COLOR_ADJUSTMENT_MIN[key], Math.min(1, value));
    }
  }
  if (color?.look) out.look = color.look;
  if (color?.duotone) out.duotone = { dark: color.duotone.dark, light: color.duotone.light };
  return out;
}

const EPSILON = 0.0005;

/** No visible change (the clip is drawn and exported untouched). */
export function isNeutralColor(color: Partial<ClipColor> | null | undefined): boolean {
  if (!color) return true;
  if (color.duotone) return false;
  return COLOR_ADJUSTMENTS.every((key) => Math.abs(color[key] ?? 0) < EPSILON);
}

/** Only the vignette is set: the colours stay untouched. */
export function hasColorMatrix(color: Partial<ClipColor> | null | undefined): boolean {
  if (!color) return false;
  if (color.duotone) return true;
  return COLOR_ADJUSTMENTS.some(
    (key) => key !== "vignette" && Math.abs(color[key] ?? 0) >= EPSILON,
  );
}

// ---------------------------------------------------------------------------
// Matrix
// ---------------------------------------------------------------------------

/**
 * Affine colour matrix, 3 rows of [r, g, b, offset] (row-major, offset in
 * 0..1 units): out.r = m[0]·r + m[1]·g + m[2]·b + m[3], and so on.
 */
export type ColorMatrix = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

const IDENTITY: ColorMatrix = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0];

/** `b ∘ a`: first a, then b. */
function compose(b: ColorMatrix, a: ColorMatrix): ColorMatrix {
  const out = [...IDENTITY] as ColorMatrix;
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 4; col++) {
      let value = col === 3 ? b[row * 4 + 3]! : 0;
      for (let k = 0; k < 3; k++) value += b[row * 4 + k]! * a[k * 4 + col]!;
      out[row * 4 + col] = value;
    }
  }
  return out;
}

const gains = (r: number, g: number, b: number, offset = 0): ColorMatrix => [
  r,
  0,
  0,
  offset,
  0,
  g,
  0,
  offset,
  0,
  0,
  b,
  offset,
];

/** Rec. 709 luma, like the saturation of CSS/SVG filters. */
const LUMA = [0.2126, 0.7152, 0.0722] as const;

/**
 * Duotone as a matrix: the luma of the pixel mixes `dark` (black) and
 * `light` (white) – an affine map like the other steps, so preview, PDF and
 * ffmpeg apply it identically.
 */
function duotoneMatrix(dark: string, light: string): ColorMatrix {
  const a = parseHex(dark);
  const b = parseHex(light);
  const row = (from: number, to: number) => {
    const span = (to - from) / 255;
    return [LUMA[0] * span, LUMA[1] * span, LUMA[2] * span, from / 255];
  };
  return [...row(a.r, b.r), ...row(a.g, b.g), ...row(a.b, b.b)] as ColorMatrix;
}

/**
 * The colour matrix of a correction. Order: white balance (temperature,
 * tint), brightness, contrast, saturation, fade.
 */
export function colorMatrix(input: Partial<ClipColor> | null | undefined): ColorMatrix {
  const color = normalizeColor(input);
  let m = IDENTITY;
  if (color.temperature !== 0 || color.tint !== 0) {
    const w = color.temperature;
    const t = color.tint;
    m = compose(
      gains(1 + 0.16 * w + 0.04 * t, 1 + 0.03 * w - 0.12 * t, 1 - 0.18 * w + 0.04 * t),
      m,
    );
  }
  if (color.brightness !== 0) {
    const b = color.brightness;
    const gain = 1 + 0.45 * b;
    m = compose(gains(gain, gain, gain, 0.05 * b), m);
  }
  if (color.contrast !== 0) {
    const k = 1 + 0.75 * color.contrast;
    m = compose(gains(k, k, k, 0.5 * (1 - k)), m);
  }
  if (color.saturation !== 0) {
    const s = 1 + color.saturation;
    const row = (index: number): number[] =>
      LUMA.map((weight, k) => (1 - s) * weight + (k === index ? s : 0));
    const [r, g, b] = [row(0), row(1), row(2)];
    m = compose([r[0]!, r[1]!, r[2]!, 0, g[0]!, g[1]!, g[2]!, 0, b[0]!, b[1]!, b[2]!, 0], m);
  }
  if (color.fade !== 0) {
    const f = color.fade;
    m = compose(gains(1 - 0.22 * f, 1 - 0.22 * f, 1 - 0.2 * f, 0.16 * f), m);
  }
  if (color.duotone) m = compose(duotoneMatrix(color.duotone.dark, color.duotone.light), m);
  return m.map((value) => Math.round(value * 1e5) / 1e5) as ColorMatrix;
}

/** Applies a matrix to one colour (0..255 per channel), clamped. */
export function applyColorMatrix(
  m: ColorMatrix,
  [r, g, b]: readonly [number, number, number],
): [number, number, number] {
  const out = [0, 1, 2].map((row) => {
    const value =
      m[row * 4]! * (r / 255) +
      m[row * 4 + 1]! * (g / 255) +
      m[row * 4 + 2]! * (b / 255) +
      m[row * 4 + 3]!;
    return Math.round(Math.max(0, Math.min(1, value)) * 255);
  });
  return out as [number, number, number];
}

/** `values` of an SVG `feColorMatrix type="matrix"` (4 × 5, alpha unchanged). */
export function svgColorMatrixValues(m: ColorMatrix): string {
  const f = (value: number) => String(Math.round(value * 1e5) / 1e5);
  return [
    ...[m[0], m[1], m[2], 0, m[3]],
    ...[m[4], m[5], m[6], 0, m[7]],
    ...[m[8], m[9], m[10], 0, m[11]],
    ...[0, 0, 0, 1, 0],
  ]
    .map((value) => f(value!))
    .join(" ");
}

/** Short stable key of a matrix (ids of preview filters). */
export function colorMatrixKey(m: ColorMatrix): string {
  let hash = 2166136261;
  for (const value of m) {
    const text = value.toFixed(4);
    for (let index = 0; index < text.length; index++) {
      hash ^= text.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
  }
  return (hash >>> 0).toString(36);
}

/** ffmpeg's colorchannelmixer takes coefficients in −2..2. */
const MIXER_LIMIT = 1.95;

/**
 * ffmpeg filters applying the matrix to BT.709 video (limited range, as the
 * export's scale filters write it): to 8-bit RGBA, the mixer (the alpha of
 * an opaque frame is 255, so its coefficient carries the offset), back to
 * yuv420p. Both conversions name BT.709 – ffmpeg's default for RGB → YUV is
 * BT.601, which would shift the colours against the preview. Matrices with
 * coefficients beyond ffmpeg's range are split into a scaled matrix and a
 * gain. Empty for a neutral correction.
 */
export function ffmpegColorFilters(color: Partial<ClipColor> | null | undefined): string[] {
  if (!hasColorMatrix(color)) return [];
  const m = colorMatrix(color);
  const largest = Math.max(...m.map((value) => Math.abs(value)));
  const split = largest > MIXER_LIMIT ? largest / MIXER_LIMIT : 1;
  const f = (value: number) => (Math.round((value / split) * 1e5) / 1e5).toString();
  const names = [
    ["rr", "rg", "rb", "ra"],
    ["gr", "gg", "gb", "ga"],
    ["br", "bg", "bb", "ba"],
  ];
  const options = names.flatMap((row, r) => row.map((name, c) => `${name}=${f(m[r * 4 + c]!)}`));
  const filters = [
    "scale=in_color_matrix=bt709:in_range=tv",
    "format=rgba",
    `colorchannelmixer=${options.join(":")}:aa=1`,
  ];
  if (split > 1) {
    let rest = split;
    while (rest > 1.0001) {
      const gain = Math.min(rest, MIXER_LIMIT);
      const g = (Math.round(gain * 1e5) / 1e5).toString();
      filters.push(`colorchannelmixer=rr=${g}:gg=${g}:bb=${g}`);
      rest /= gain;
    }
  }
  filters.push("scale=out_color_matrix=bt709:out_range=tv", "format=yuv420p");
  return filters;
}

// ---------------------------------------------------------------------------
// Vignette
// ---------------------------------------------------------------------------

export interface VignetteStop {
  /** 0 = centre, 1 = corner of the area (elliptical). */
  offset: number;
  /** Opacity of black. */
  alpha: number;
}

/** Stops of the vignette gradient (empty without vignette). */
export function vignetteStops(strength: number | undefined): VignetteStop[] {
  const v = Math.max(0, Math.min(1, strength ?? 0));
  if (v < EPSILON) return [];
  const inner = 0.62 - 0.22 * v;
  const stops: VignetteStop[] = [{ offset: 0, alpha: 0 }];
  for (let step = 0; step <= 6; step++) {
    const p = step / 6;
    const eased = p * p * (3 - 2 * p);
    stops.push({
      offset: Math.round((inner + (1 - inner) * p) * 1000) / 1000,
      alpha: Math.round(0.82 * v * eased * 1000) / 1000,
    });
  }
  return stops;
}

/**
 * The vignette as SVG of an image of `width` × `height` (black, transparent
 * in the centre) over `area` (default: the whole image), with its rounded
 * corners. The gradient spans the area's bounding box, so it is elliptical
 * like on the canvas; parts of the area outside the image are cut off.
 */
export function vignetteSvg(
  width: number,
  height: number,
  strength: number,
  area?: { x: number; y: number; width: number; height: number },
  radius = 0,
): string {
  const stops = vignetteStops(strength)
    .map((stop) => `<stop offset="${stop.offset}" stop-color="#000" stop-opacity="${stop.alpha}"/>`)
    .join("");
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const box = area ?? { x: 0, y: 0, width: w, height: h };
  const r = Math.max(0, Math.min(radius, box.width / 2, box.height / 2));
  const f = (value: number) => value.toFixed(2);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><defs><radialGradient id="v" cx="0.5" cy="0.5" r="0.7071" gradientUnits="objectBoundingBox">${stops}</radialGradient></defs><rect x="${f(box.x)}" y="${f(box.y)}" width="${f(box.width)}" height="${f(box.height)}" rx="${f(r)}" fill="url(#v)"/></svg>`;
}

/** The vignette as CSS background (thumbnails; same ellipse as the SVG and the canvas). */
export function vignetteCss(strength: number): string | undefined {
  const stops = vignetteStops(strength);
  if (stops.length === 0) return undefined;
  return `radial-gradient(ellipse farthest-corner at 50% 50%, ${stops
    .map((stop) => `rgba(0,0,0,${stop.alpha}) ${Math.round(stop.offset * 1000) / 10}%`)
    .join(", ")})`;
}

// ---------------------------------------------------------------------------
// Looks (presets)
// ---------------------------------------------------------------------------

export interface ColorLook {
  id: string;
  label: string;
  values: Partial<Omit<ClipColor, "look">>;
}

/** Built-in looks of the gallery ("Original" resets). */
export const COLOR_LOOKS: readonly ColorLook[] = [
  { id: "original", label: "Original", values: {} },
  { id: "lebendig", label: "Lebendig", values: { saturation: 0.35, contrast: 0.15 } },
  {
    id: "hell",
    label: "Hell & frisch",
    values: { brightness: 0.22, contrast: -0.05, saturation: 0.12 },
  },
  { id: "warm", label: "Warm", values: { temperature: 0.45, saturation: 0.08 } },
  {
    id: "sonnig",
    label: "Sonnig",
    values: { brightness: 0.12, temperature: 0.3, saturation: 0.22 },
  },
  { id: "kuehl", label: "Kühl", values: { temperature: -0.45, tint: -0.05, contrast: 0.05 } },
  {
    id: "kino",
    label: "Kino",
    values: { contrast: 0.25, saturation: -0.15, temperature: -0.15, vignette: 0.45 },
  },
  {
    id: "vintage",
    label: "Vintage",
    values: { fade: 0.35, temperature: 0.35, saturation: -0.25, vignette: 0.35 },
  },
  { id: "verblasst", label: "Verblasst", values: { fade: 0.5, contrast: -0.15, saturation: -0.2 } },
  {
    id: "pastell",
    label: "Pastell",
    values: { brightness: 0.12, fade: 0.25, saturation: -0.3, tint: 0.1 },
  },
  {
    id: "dramatisch",
    label: "Dramatisch",
    values: { contrast: 0.45, saturation: -0.1, brightness: -0.08, vignette: 0.6 },
  },
  {
    id: "nacht",
    label: "Nacht",
    values: { temperature: -0.6, brightness: -0.15, saturation: -0.2, contrast: 0.15 },
  },
  { id: "schwarzweiss", label: "Schwarzweiß", values: { saturation: -1, contrast: 0.1 } },
  {
    id: "noir",
    label: "Noir",
    values: { saturation: -1, contrast: 0.5, brightness: -0.05, vignette: 0.6 },
  },
  {
    id: "duoton-blau",
    label: "Duoton Blau",
    values: { duotone: { dark: "#1e2a78", light: "#9fe7ff" } },
  },
  {
    id: "duoton-pink",
    label: "Duoton Pink",
    values: { duotone: { dark: "#3b0a57", light: "#ff9ecf" } },
  },
  {
    id: "duoton-gruen",
    label: "Duoton Grün",
    values: { duotone: { dark: "#0b3d2e", light: "#d9f99d" } },
  },
  {
    id: "duoton-sonne",
    label: "Duoton Sonne",
    values: { duotone: { dark: "#7a1f0b", light: "#ffd166" } },
  },
];

/**
 * Duotone looks in the colours of a brand kit (primary → background, primary
 * → accent). The colours are copied when the look is applied.
 */
export function brandDuotoneLooks(colors: readonly { key: string; color: string }[]): ColorLook[] {
  const get = (key: string) => {
    const hex = colors.find((entry) => entry.key === key)?.color;
    return hex ? opaqueHex(hex) : undefined;
  };
  const primary = get("primary");
  if (!primary) return [];
  const looks: ColorLook[] = [
    {
      id: "duoton-marke",
      label: "Duoton Marke",
      values: { duotone: { dark: primary, light: get("background") ?? "#ffffff" } },
    },
  ];
  const accent = get("accent");
  if (accent) {
    looks.push({
      id: "duoton-marke-akzent",
      label: "Duoton Akzent",
      values: { duotone: { dark: primary, light: accent } },
    });
  }
  return looks;
}

export function getColorLook(id: string): ColorLook | undefined {
  return COLOR_LOOKS.find((look) => look.id === id);
}

/** The correction of a look (built-in or saved: `{ id, values }`). */
export function colorFromLook(look: Pick<ColorLook, "id" | "values">): ClipColor | undefined {
  if (look.id === "original") return undefined;
  return { ...normalizeColor(look.values), look: look.id };
}

/** Values to store: neutral corrections are dropped (null), the look id is kept. */
export function storedColor(color: Partial<ClipColor> | null | undefined): ClipColor | undefined {
  if (!color || isNeutralColor(color)) return undefined;
  return normalizeColor(color);
}
